import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import {
  BarChart3,
  Copy,
  Eye,
  LayoutGrid,
  LayoutList,
  Lock,
  MessageSquare,
  Pencil,
  Plus,
  QrCode,
  Trash2,
} from 'lucide-react';
import dbUrl from '../../utils/credentials';
import decodeImg from '../../utils';
import PageHeader from '../../components/layout/PageHeader';
import ShareInvitationDialog from '../../components/invitation/ShareInvitationDialog';
import InvitationClosureDialog from '../../components/invitation/InvitationClosureDialog';
import { Button } from '../../components/ui/button';
import { cn } from '../../lib/utils';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent } from '../../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  tableRowClassName,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { TableActions, TableActionButton, tableActionsColumnClass } from '../../components/ui/table-actions';
import { CardListSkeleton, TableSkeleton, PageHeaderSkeleton, PAGE_SHELL, Skeleton } from '../../components/ui/skeleton';
import { getResponseCount } from '../../lib/responses';
import { isInvitationOpen, descriptionToPlainText } from '../../lib/invitation';
import { toast } from '../../lib/toast';

function AllInvitations() {
  const [invitations, setInvitations] = useState([]);
  const [responseCounts, setResponseCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [shareTarget, setShareTarget] = useState(null);
  const [closureTarget, setClosureTarget] = useState(null);
  const [viewMode, setViewMode] = useState('table');

  useEffect(() => {
    getAllInvitations();
  }, []);

  const getAllInvitations = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${dbUrl}invitations.json`);
      let data = response.data
        ? Object.entries(response.data).map(([id, inv]) => ({ id, ...inv }))
        : [];
      const withCreatedAt = data.filter((inv) => inv.createdAt).sort((a, b) => b.createdAt - a.createdAt);
      const withoutCreatedAt = data.filter((inv) => !inv.createdAt);
      const sorted = [...withCreatedAt, ...withoutCreatedAt];
      setInvitations(sorted);

      const counts = {};
      await Promise.all(
        sorted.map(async (inv) => {
          counts[inv.id] = await getResponseCount(inv.id, inv.responses);
        })
      );
      setResponseCounts(counts);
    } catch (error) {
      console.error('error fetching all invitations', error);
      toast.error('Errore di caricamento', 'Impossibile recuperare l\'elenco inviti.');
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await axios.delete(`${dbUrl}invitations/${deleteTarget.id}.json`);
      setInvitations((prev) => prev.filter((inv) => inv.id !== deleteTarget.id));
      toast.success('Invito eliminato', `"${deleteTarget.name}" è stato rimosso.`);
    } catch (error) {
      console.error('delete error', error);
      toast.error('Eliminazione non riuscita', 'Riprova tra qualche istante.');
    }
    setDeleteTarget(null);
  };

  const handleClosureSaved = (invitationId, { isOpen, closesAt }) => {
    setInvitations((prev) =>
      prev.map((inv) =>
        inv.id === invitationId
          ? { ...inv, isOpen, closesAt, updatedAt: Date.now() }
          : inv
      )
    );
    setClosureTarget(null);
  };

  const renderThumb = (img) => {
    const image = decodeImg(img);
    if (!image) return <span className="text-[var(--ba-muted)]">—</span>;
    return <div className="mx-auto max-h-12 max-w-[60px] overflow-hidden">{image}</div>;
  };

  const renderActions = (inv, hasResponses) => (
    <TableActions>
      <TableActionButton
        label="Gestisci iscrizioni (apri/chiudi)"
        icon={Lock}
        onClick={() => setClosureTarget(inv)}
      />
      <TableActionButton label="Condividi (link / QR)" icon={QrCode} onClick={() => setShareTarget(inv)} />
      <TableActionButton label="Duplica invito" icon={Copy} onClick={() => { window.location.href = `/editor-invito?duplicate=${inv.id}`; }} />
      <TableActionButton
        label={hasResponses ? 'Modifica (solo nascondi campi)' : 'Modifica invito'}
        icon={Pencil}
        onClick={() => { window.location.href = `/editor-invito?id=${inv.id}`; }}
      />
      <TableActionButton label="Anteprima invito" icon={Eye} onClick={() => window.open(`/invito?id=${inv.id}`, '_blank')} />
      <TableActionButton label="Visualizza risposte" icon={MessageSquare} onClick={() => { window.location.href = `/risposte?id=${inv.id}`; }} />
      <TableActionButton
        label="Statistiche risposte"
        icon={BarChart3}
        disabled={!hasResponses}
        onClick={() => { window.location.href = `/risposte/statistiche?id=${inv.id}`; }}
      />
      <TableActionButton label="Elimina invito" icon={Trash2} variant="destructive" onClick={() => setDeleteTarget(inv)} />
    </TableActions>
  );

  return (
    <div className={PAGE_SHELL}>
      {loading ? (
        <>
          <PageHeaderSkeleton />
          <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
            <Skeleton className="h-4 w-28" />
            <Skeleton className="h-10 w-48 rounded-lg" />
          </div>
          {viewMode === 'table' ? (
            <TableSkeleton columns={6} rows={8} />
          ) : (
            <CardListSkeleton count={5} />
          )}
        </>
      ) : (
        <>
      <PageHeader
        title="Tutti gli inviti"
        description="Visualizza, modifica o elimina gli inviti creati. Puoi chiudere le iscrizioni in qualsiasi momento; la modifica completa resta bloccata se ci sono risposte."
      />

      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <Tabs value={viewMode} onValueChange={setViewMode}>
          <TabsList className="inline-flex h-auto w-max flex-nowrap justify-start gap-0.5 p-1">
            <TabsTrigger value="table" className="shrink-0 px-3">
              <LayoutList className="h-4 w-4" />
              Tabella
            </TabsTrigger>
            <TabsTrigger value="cards" className="shrink-0 px-3">
              <LayoutGrid className="h-4 w-4" />
              Card
            </TabsTrigger>
          </TabsList>
        </Tabs>
        <Button asChild>
          <Link to="/editor-invito">
            <Plus className="h-4 w-4" />
            Nuovo invito
          </Link>
        </Button>
      </div>

      {invitations.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center ba-muted">
            Nessun invito presente.{' '}
            <Link to="/editor-invito" className="font-semibold text-brand-orange hover:underline">
              Creane uno
            </Link>
          </CardContent>
        </Card>
      ) : viewMode === 'table' ? (
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-[180px]">Nome</TableHead>
                  <TableHead className="min-w-[100px]">Risposte</TableHead>
                  <TableHead className="min-w-[80px]">Campi</TableHead>
                  <TableHead className="min-w-[100px]">Stato</TableHead>
                  <TableHead className="min-w-[120px]">Creato</TableHead>
                  <TableHead className={tableActionsColumnClass({ minWidth: 'min-w-[19rem]' })}>
                    Azioni
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                <AnimatePresence initial={false} mode="popLayout">
                  {invitations.map((inv, index) => {
                    const responseCount = responseCounts[inv.id] ?? 0;
                    const open = isInvitationOpen(inv);
                    return (
                      <motion.tr
                        key={inv.id}
                        layout
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -6 }}
                        transition={{ duration: 0.2, delay: index * 0.02 }}
                        className={tableRowClassName}
                      >
                        <TableCell className="font-medium">{inv.name || '—'}</TableCell>
                        <TableCell>{responseCount}</TableCell>
                        <TableCell>{inv.fields?.length || 0}</TableCell>
                        <TableCell>
                          <Badge variant={open ? 'default' : 'secondary'}>{open ? 'Aperto' : 'Chiuso'}</Badge>
                        </TableCell>
                        <TableCell className="text-sm text-[var(--ba-muted)]">
                          {inv.createdAt ? new Date(inv.createdAt).toLocaleDateString('it-IT') : '—'}
                        </TableCell>
                        <TableCell className={tableActionsColumnClass({ minWidth: 'min-w-[19rem]' })}>
                          {renderActions(inv, responseCount > 0)}
                        </TableCell>
                      </motion.tr>
                    );
                  })}
                </AnimatePresence>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {invitations.map((inv, i) => {
            const plainDesc = descriptionToPlainText(inv.description || inv.text || '');
            const responseCount = responseCounts[inv.id] ?? 0;
            const hasResponses = responseCount > 0;
            const open = isInvitationOpen(inv);
            const createdLabel = inv.createdAt ? new Date(inv.createdAt).toLocaleDateString('it-IT') : '—';
            const updatedLabel = inv.updatedAt ? new Date(inv.updatedAt).toLocaleDateString('it-IT') : null;
            const closesAtTime = inv.closesAt ? new Date(inv.closesAt).getTime() : null;
            const daysToClose =
              open && closesAtTime ? Math.ceil((closesAtTime - Date.now()) / (1000 * 60 * 60 * 24)) : null;
            const isClosingSoon = open && daysToClose != null && daysToClose >= 0 && daysToClose <= 3;

            const statusLabel = open ? (isClosingSoon ? 'In scadenza' : 'Aperto') : 'Chiuso';
            const statusVariant = open ? (isClosingSoon ? 'outline' : 'default') : 'secondary';
            const statusStripClass = open
              ? (isClosingSoon ? 'bg-brand-gold' : 'bg-brand-green')
              : 'bg-brand-danger';

            const thumb = inv.image || inv.logo;
            const thumbNode = renderThumb(thumb);
            const initials = (inv.name || 'Invito')
              .split(' ')
              .filter(Boolean)
              .slice(0, 2)
              .map((w) => w[0]?.toUpperCase())
              .join('');

            return (
              <motion.div
                key={inv.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.03 }}
              >
                <Card className="relative overflow-hidden transition-shadow hover:shadow-md">
                  <div className={cn('absolute inset-y-0 left-0 w-1.5', statusStripClass)} />
                  <CardContent className="flex h-full flex-col gap-3 p-4 pl-5">
                    <div className="flex flex-1 flex-col gap-3">
                      <div className="flex items-start gap-3">
                      <div className="shrink-0">
                        {thumbNode ? (
                          <div className="h-14 w-14 overflow-hidden rounded-xl border border-[var(--ba-surface-border)] bg-[var(--ba-surface-bg)]">
                            {thumbNode}
                          </div>
                        ) : (
                          <div className="flex h-14 w-14 items-center justify-center rounded-xl border border-[var(--ba-surface-border)] bg-gradient-to-br from-brand-orange/20 to-brand-green/20 text-sm font-bold text-brand-green">
                            {initials}
                          </div>
                        )}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-start justify-between gap-2">
                          <h3 className="line-clamp-2 min-w-0 flex-1 font-bold leading-snug">
                            {inv.name || '—'}
                          </h3>
                          <Badge variant={statusVariant}>{statusLabel}</Badge>
                        </div>

                        <p className="mt-1 line-clamp-1 text-sm text-[var(--ba-muted)]">
                          {plainDesc || 'Nessuna descrizione'}
                        </p>
                      </div>
                      </div>

                      <div className="grid grid-cols-2 gap-2">
                      <div className="rounded-lg border border-[var(--ba-surface-border)] bg-black/[0.02] px-3 py-2 dark:bg-white/[0.02]">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ba-muted)]">Risposte</p>
                        <p className="text-xl font-bold tabular-nums text-brand-orange">{responseCount}</p>
                      </div>
                      <div className="rounded-lg border border-[var(--ba-surface-border)] bg-black/[0.02] px-3 py-2 dark:bg-white/[0.02]">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ba-muted)]">Campi</p>
                        <p className="text-xl font-bold tabular-nums">{inv.fields?.length || 0}</p>
                      </div>
                      <div className="rounded-lg border border-[var(--ba-surface-border)] bg-black/[0.02] px-3 py-2 dark:bg-white/[0.02]">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ba-muted)]">Creato</p>
                        <p className="text-sm font-semibold tabular-nums">{createdLabel}</p>
                      </div>
                      <div className="rounded-lg border border-[var(--ba-surface-border)] bg-black/[0.02] px-3 py-2 dark:bg-white/[0.02]">
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--ba-muted)]">
                          {updatedLabel ? 'Aggiornato' : 'Ultima att.'}
                        </p>
                        <p className="text-sm font-semibold tabular-nums">{updatedLabel || '—'}</p>
                      </div>
                      </div>

                      <div className="min-h-[2.25rem]">
                      <div className="flex flex-wrap items-center gap-2 text-xs text-[var(--ba-muted)]">
                        {hasResponses && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-[var(--ba-surface-border)] bg-[var(--ba-accent-orange-subtle)] px-2 py-1 font-medium text-brand-orange">
                            Statistiche disponibili
                          </span>
                        )}
                        {isClosingSoon && daysToClose != null && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-[var(--ba-surface-border)] bg-[var(--ba-accent-green-subtle)] px-2 py-1 font-medium text-brand-green">
                            Chiude tra {daysToClose}g
                          </span>
                        )}
                        {!hasResponses && !isClosingSoon && (
                          <span className="inline-flex items-center gap-1 rounded-full border border-[var(--ba-surface-border)] bg-black/[0.02] px-2 py-1 font-medium text-[var(--ba-muted)] dark:bg-white/[0.02]">
                            Nessun insight disponibile
                          </span>
                        )}
                      </div>
                      </div>
                    </div>

                    <div className="mt-1 border-t border-[var(--ba-surface-border)] pt-3">
                      <div className="grid grid-cols-2 gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full justify-center gap-2"
                          onClick={() => { window.location.href = `/risposte?id=${inv.id}`; }}
                        >
                          <MessageSquare className="h-4 w-4" />
                          Risposte
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          className="w-full justify-center gap-2"
                          disabled={!hasResponses}
                          onClick={() => { window.location.href = `/risposte/statistiche?id=${inv.id}`; }}
                        >
                          <BarChart3 className="h-4 w-4" />
                          Statistiche
                        </Button>
                      </div>
                      <div className="mt-2 flex justify-end">
                        {renderActions(inv, hasResponses)}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

        </>
      )}

      <ShareInvitationDialog
        open={!!shareTarget}
        onOpenChange={(open) => !open && setShareTarget(null)}
        invitationId={shareTarget?.id}
        invitationName={shareTarget?.name}
      />

      <InvitationClosureDialog
        open={!!closureTarget}
        onOpenChange={(open) => !open && setClosureTarget(null)}
        invitation={closureTarget}
        onSaved={handleClosureSaved}
      />

      <Dialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Eliminare l&apos;invito?</DialogTitle>
            <DialogDescription>
              Stai per eliminare &quot;{deleteTarget?.name}&quot;. Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Annulla</Button>
            <Button variant="destructive" onClick={confirmDelete}>Elimina</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default AllInvitations;
